import { useEffect, useRef, useState } from "react";
import { useSettlementStore } from "../store";
import { BorderRectProps } from "../plugins/border-rect/dto";
import { generateSelection } from "../utils";
import { useLatest } from "ahooks";
import { Selection } from "../dto";

const useSelectionBorderRect = (onChange?: (cellIds: Selection) => void) => {
  const selectIdRef = useRef<string>();
  const [rect, setRect] =
    useState<Omit<BorderRectProps, "visible" | "color">>();
  const [{ action, config, selection }, { setAction, setSelection }] =
    useSettlementStore();
  const tableRef = useRef<HTMLTableElement>();
  const configRef = useLatest(config);

  const onSelect = (startCellId: string, endCellId: string) => {
    const currentSelection = generateSelection(
      configRef.current,
      startCellId,
      endCellId
    );
    setSelection(currentSelection);
    onChange?.(currentSelection);
  };

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLTableCellElement;
      const cell = target.closest("td[data-id]") as HTMLTableCellElement;
      if (!cell || action !== "none") return;
      setAction("select");
      selectIdRef.current = cell.dataset.id;
      onSelect(selectIdRef.current, selectIdRef.current);
    };
    const onMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLTableCellElement;
      const cell = target.closest("td[data-id]") as HTMLTableCellElement;
      if (!cell) return;
      const { id } = cell.dataset;
      if (selectIdRef.current && id) {
        onSelect(selectIdRef.current, id);
      }
    };
    const onMouseUp = () => {
      selectIdRef.current = null;
      setAction("none");
    };
    tableRef.current.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      tableRef.current?.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const reset = () => {
    const { rect: selectionRect } = selection;
    if (!selectionRect || !tableRef.current) return;
    const startCell = tableRef.current.querySelector(
      `td[data-id="${selectionRect.startId}"]`
    );
    const endCell = tableRef.current.querySelector(
      `td[data-id="${selectionRect.endId}"]`
    );
    if (!startCell || !endCell) return;
    const { x: startX, y: startY } = startCell.getBoundingClientRect();
    const { x: endX, y: endY, width, height } = endCell.getBoundingClientRect();
    const { x, y } = tableRef.current.getBoundingClientRect();
    setRect({
      x: startX - x,
      y: startY - y,
      width: endX - startX + width,
      height: endY - startY + height,
    });
  };

  useEffect(reset, [JSON.stringify(selection.rect), JSON.stringify(config)]);

  const setRef = (node) => {
    tableRef.current = node;
  };

  return [
    setRef,
    {
      visible: !!selection.rect,
      ...rect,
      color: "#3B65EE",
    },
  ] as const;
};

export default useSelectionBorderRect;
